import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { loginresponse } from '../graphql/types/loginresponse.type';
import { LOGIN_MUTATION } from '../graphql/mutations/login.mutation';
import { AZURE_LOGIN_MUTATION } from '../graphql/mutations/azure-login.mutation';
import { environment } from '../../../environments/environment';
import { MsalService } from '@azure/msal-angular';
import { Observable, from, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  token: any;
  isAuthenticated = false;

  constructor(
    private apollo: Apollo,
    private msalService: MsalService,
  ) {}

  login(email: string, password: string) {
    return this.apollo.mutate<loginresponse>({
      mutation: LOGIN_MUTATION,
      errorPolicy: 'all',
      variables: {
        email,
        password,
      },
    });
  }

  /**
   * Initiates Azure AD login flow using redirect
   */
  loginWithAzure(): Observable<void> {
    console.log(
      'Initiating Azure AD login flow: Redirecting to Azure AD',
      environment.azureAd.redirectUri,
    );

    // Determine the correct redirect URI based on current URL
    let redirectUri = environment.azureAd.redirectUri;

    console.log('Current URL:', window.location.href);

    if (window.location.href.includes('app.stacksoftware.com')) {
      redirectUri = redirectUri.replace(
        'prod.stacksoftware.com',
        'app.stacksoftware.com',
      );
    }

    if (window.location.href.includes('app.nextfieldservice.com')) {
      redirectUri = redirectUri.replace(
        'dev.nextfieldservice.com',
        'app.nextfieldservice.com',
      );
    }

    console.log('Using redirect URI:', redirectUri);

    // Use the MsalService's loginRedirect method
    return from(
      this.msalService.loginRedirect({
        scopes: environment.azureAd.scopes,
        prompt: 'select_account',
        redirectUri: redirectUri,
      }),
    ).pipe(
      map(() => {
        // loginRedirect doesn't return a response - it redirects the browser
        // The response will be handled in AppComponent after the redirect
        return;
      }),
      catchError((error) => {
        console.error('Azure login redirect error:', error);
        throw error;
      }),
    );
  }

  /**
   * Checks if there's an active Azure AD session
   */
  checkAzureSession(): Observable<boolean> {
    return of(this.msalService.instance.getAllAccounts().length > 0);
  }

  /**
   * Gets the active Azure AD account
   */
  getActiveAccount() {
    return this.msalService.instance.getActiveAccount();
  }

  /**
   * Gets all Azure AD accounts
   */
  getAllAccounts() {
    return this.msalService.instance.getAllAccounts();
  }

  /**
   * Process the Azure AD redirect with the ID token
   * @param idToken The ID token from Azure AD
   */
  processAzureRedirect(idToken: string): Observable<any> {
    console.log('Processing Azure redirect with token');

    if (!idToken) {
      console.error('No token provided to processAzureRedirect');
      return new Observable((observer) => {
        observer.error(new Error('No token provided'));
        observer.complete();
      });
    }

    // Call our backend with the token
    return this.apollo.mutate<loginresponse>({
      mutation: AZURE_LOGIN_MUTATION,
      variables: {
        token: idToken,
      },
    });
  }

  /**
   * Logout from Azure AD
   */
  async logout() {
    // Clear Apollo cache
    await this.apollo.client.clearStore();

    // If there's an active Azure account, logout from MSAL as well
    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      // Clear the active account
      this.msalService.instance.setActiveAccount(null);
    }
  }

  /**
   * Logout from Azure AD with redirect
   */
  logoutWithAzure(): Observable<void> {
    return from(this.msalService.logoutRedirect());
  }
}
